import { APITransport } from '@client/transports/api';

const BINDING_KEYS = ['googlegroupbindings', 'o365groupbindings', 'oktagroupbindings', 'csvtreenodebindings'];

async function getRouteBindings() {
  // Roles without permission on these endpoints get a 405, which the guards below already
  // degrade to "no bindings" — so 4xx is an expected condition, not a fault worth capturing.
  const [allRoutesResponse, groupsResponse] = await Promise.all([
    APITransport.get({ path: '/rest/domain/bizcardroutes', ignoreClientErrors: true }),
    APITransport.get({ path: '/rest/domain/sendergroups', ignoreClientErrors: true })
  ]);

  const routes = Array.isArray(allRoutesResponse) ? allRoutesResponse : [];
  const groups = Array.isArray(groupsResponse) ? groupsResponse : [];

  const bindings = Object.fromEntries(BINDING_KEYS.map((key) => [key, []]));
  const directSenders = [];
  let hasAllUsers = false;

  for (const route of routes) {
    for (const routeGroup of route.sendergroups || []) {
      const group = groups.find((g) => g.groupname === routeGroup.groupname);
      if (!group) continue;

      if (group.senders?.some((s) => s.username === '*')) {
        hasAllUsers = true;
      }

      for (const sender of group.senders || []) {
        if (sender.username && sender.username !== '*') {
          directSenders.push({ username: sender.username });
        }
      }

      for (const key of BINDING_KEYS) {
        if (group[key]?.length) {
          bindings[key].push(...group[key]);
        }
      }
    }
  }

  return { bindings, directSenders, hasAllUsers };
}

export const BizCardsService = {
  delete(body) {
    return APITransport.post({
      path: '/rest/domain/delete/bizcards',
      body
    });
  },
  async getAll() {
    const response = await APITransport.get({
      path: '/rest/domain/bizcards'
    });

    return Array.isArray(response) ? response : [];
  },
  async getById(id) {
    const cards = await this.getAll();
    return cards.find(({ rulename }) => rulename === id);
  },
  preview(body) {
    return APITransport.post({
      path: '/rest/domain/execute/renderbizcard',
      body
    });
  },
  update(body) {
    return APITransport.post({
      path: '/rest/domain/save/bizcards',
      body
    });
  },
  async getFields(query) {
    const userParams = await APITransport.get({
      path: '/rest/listofvalues/listuserparams',
      query
    });

    const params = Array.isArray(userParams) ? userParams : [];

    return [...new Set(params.map((param) => param.name))].sort();
  },
  async getUserBindings() {
    const { bindings, directSenders, hasAllUsers } = await getRouteBindings();

    if (hasAllUsers) return null;

    const result = {};

    if (directSenders.length) {
      result.csvfilter = { data: directSenders, errors: [] };
    }

    for (const [key, value] of Object.entries(bindings)) {
      if (value.length) result[key] = value;
    }

    return result;
  },
  async getUsersByBizcard(bizcardId, params = {}) {
    const routesResponse = await APITransport.get({ path: '/rest/domain/bizcardroutes' });
    const routes = Array.isArray(routesResponse) ? routesResponse : [];

    // Resolve each route separately and union the results. `_search_users` ANDs the filters
    // in a single request, so folding several routes' bindings into one body intersects
    // their members instead of unioning them — a bizcard served by both a direct-sender
    // group and a group binding would come back empty. Per-route resolution also keeps this
    // list in step with the user counts getAssignments() shows on the assignment page.
    const activeRoutes = routes.filter((r) => r.status && r.blockname === bizcardId);

    const responses = await Promise.all(
      activeRoutes.map((route) =>
        this.getUsersByGroups(
          (route.sendergroups || []).map((sg) => sg.groupname),
          params
        )
      )
    );

    const byEmail = new Map();

    for (const response of responses) {
      for (const user of response?.data?.data || response?.data || []) {
        const email = user.email || user.username;
        if (email && !byEmail.has(email)) byEmail.set(email, user);
      }
    }

    return { data: [...byEmail.values()] };
  },
  async getUsers(params = {}) {
    const routeBindings = await this.getUserBindings();

    const searchBody = {
      perPage: 10000,
      systemFilter: [],
      ...params,
      ...routeBindings
    };

    return APITransport.post({
      path: '/rest/domain/search/users',
      includeResponseMeta: true,
      body: searchBody
    });
  },
  async getAssignments() {
    const [routesResponse, usersResponse] = await Promise.all([
      APITransport.get({ path: '/rest/domain/bizcardroutes' }),
      APITransport.post({
        path: '/rest/domain/search/users',
        includeResponseMeta: true,
        body: { perPage: 1, page: 0, systemFilter: [] }
      })
    ]);

    const routes = Array.isArray(routesResponse) ? routesResponse : [];
    const totalUsers = Number(usersResponse?.headers?.['x-search-total']) || 0;

    // Routes are returned in priority order. Process them one-by-one so each user is
    // claimed by the first (highest-priority) route that matches them, not by whichever
    // bizcard happens to also include a broad group like allusers in a later route.
    const activeRoutes = routes.filter((r) => r.status && r.blockname);

    const usersByRoute = await Promise.all(
      activeRoutes.map((route) => this.getUsersByGroups((route.sendergroups || []).map((sg) => sg.groupname)))
    );

    const bizcardOrder = [];
    const bizcardMeta = {};
    const claimed = new Set();

    for (let i = 0; i < activeRoutes.length; i++) {
      const route = activeRoutes[i];
      const { blockname } = route;
      const groupNames = (route.sendergroups || []).map((sg) => sg.groupname);

      if (!bizcardMeta[blockname]) {
        bizcardMeta[blockname] = { name: blockname, groups: [], users: [], userCount: 0 };
        bizcardOrder.push(blockname);
      }

      bizcardMeta[blockname].groups = [...new Set([...bizcardMeta[blockname].groups, ...groupNames])];

      const response = usersByRoute[i];
      const users = response?.data?.data || response?.data || [];

      for (const user of users) {
        const email = user.email || user.username;
        if (email && !claimed.has(email)) {
          claimed.add(email);
          bizcardMeta[blockname].users.push(email);
          bizcardMeta[blockname].userCount++;
        }
      }
    }

    const assignments = bizcardOrder.map((name) => bizcardMeta[name]).filter((a) => a.userCount > 0);
    const assignedUsers = assignments.reduce((sum, a) => sum + a.userCount, 0);
    const unassigned = Math.max(0, totalUsers - assignedUsers);

    return { assignments, totalUsers, assignedUsers, unassigned };
  },
  async getUsersByGroups(groupnames, params = {}) {
    const allGroups = await APITransport.get({ path: '/rest/domain/sendergroups' });
    const selected = allGroups.filter((g) => groupnames.includes(g.groupname));

    const bindings = Object.fromEntries(BINDING_KEYS.map((key) => [key, []]));
    const directSenders = [];
    const dynamicGroupNames = [];
    let hasAllUsers = false;

    for (const group of selected) {
      // Dynamic groups carry no stored senders/bindings; their membership comes from a
      // live directory query (userAttrs) resolved via the dynamicmembers endpoint below.
      if (group.userAttrs?.length) {
        dynamicGroupNames.push(group.groupname);
      }

      for (const sender of group.senders || []) {
        if (sender.username === '*') {
          hasAllUsers = true;
        } else if (sender.username) {
          directSenders.push({ username: sender.username });
        }
      }

      for (const key of BINDING_KEYS) {
        if (group[key]?.length) {
          bindings[key].push(...group[key]);
        }
      }
    }

    if (hasAllUsers) {
      return this.getUsers(params);
    }

    // dynamicmembers returns an array of member emails (or 'Empty'/null when unmatched).
    const dynamicUsers = (
      await Promise.all(
        dynamicGroupNames.map((groupname) =>
          APITransport.post({ path: '/rest/domain/execute/dynamicmembers', body: { groupname } })
        )
      )
    )
      .filter(Array.isArray)
      .flat()
      .map((email) => ({ email }));

    const hasBindings = directSenders.length || Object.values(bindings).some((v) => v.length);
    if (!hasBindings) {
      return { data: dynamicUsers };
    }

    const groupBindings = {};

    if (directSenders.length) {
      groupBindings.csvfilter = { data: directSenders, errors: [] };
    }

    for (const [key, value] of Object.entries(bindings)) {
      if (value.length) groupBindings[key] = value;
    }

    const response = await APITransport.post({
      path: '/rest/domain/search/users',
      includeResponseMeta: true,
      body: {
        perPage: 10000,
        systemFilter: [],
        ...params,
        ...groupBindings
      }
    });

    // When a selection mixes dynamic and binding-based groups, fold the dynamic members
    // into the binding results, deduped by email.
    if (dynamicUsers.length) {
      const bindingUsers = response?.data?.data || response?.data || [];
      const byEmail = new Map();
      for (const user of [...bindingUsers, ...dynamicUsers]) {
        if (user.email && !byEmail.has(user.email)) byEmail.set(user.email, user);
      }
      return { data: [...byEmail.values()] };
    }

    return response;
  }
};
